import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ACTIONS_ID } from "../PageHeaderActions/PageHeaderActions";
import s from "./BreadcrumbHistory.module.scss";

/** Friendly labels for LoveStrong routes */
const ROUTE_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  "admin-dashboard": "Admin Dashboard",
  quickReport: "Quick Reports",
  search_data: "Advanced Search",
  manageCouples: "Manage Couples",
  manageRetreat: "Manage Retreats",
  "retreat-closure-list": "Retreat Closure",
  sendEmail: "Broadcast Emails",
  logreport: "Log Report",
  help: "Help",
  tickets: "Tickets",
  "create-ticket": "Create Ticket",
  ticketReply: "Ticket Reply",
  parish: "Parishes",
  service: "Services",
  roles: "Roles",
  talks: "Talks",
  parishCategories: "Parish Categories",
  deaneries: "Deaneries",
  "sample-content": "Sample Content",
  "missing-data": "Missing Parish Data",
  "participated-in-retreats": "Participated In Retreats",
  "update-couple": "Update Your Profile",
  "my-profile": "My Profile",
  "retreat-setup": "Retreat Setup",
  retreats: "Retreats",
  "register-new-retreat": "Project a Future Retreat",
  project: "Project a Future Retreat",
  "create-program": "Create Program",
  "create-directory": "Create Directory",
  "retreat-backup": "Retreat Backup",
  "build-my-team": "Build My Team",
  "priest-list": "Priest List",
  "news-letter-link": "Newsletter Link",
  "upcoming-retreats": "Upcoming Retreats",
  "register-waitList": "Wait List",
  "registration-retreat": "Registration",
  "Access-SuperAdmin": "Access Super Admin",
  "couple-update": "Couple Update",
  directory: "Directory",
  program: "Program",
  retreatRoster: "Retreat Roster",
  thankyou: "Thank You",
};

/** Parent section crumbs for nested menu pages */
const ROUTE_PARENTS: Record<string, Array<{ label: string; to?: string }>> = {
  parish: [{ label: "Master Data" }],
  service: [{ label: "Master Data" }],
  roles: [{ label: "Master Data" }],
  talks: [{ label: "Master Data" }],
  parishCategories: [{ label: "Master Data" }],
  deaneries: [{ label: "Master Data" }],
  "sample-content": [{ label: "Master Data" }],
  "missing-data": [{ label: "Master Report Section" }],
  "retreat-setup": [{ label: "Manage Retreats", to: "/manageRetreat" }],
  retreats: [{ label: "Manage Retreats", to: "/manageRetreat" }],
  "register-new-retreat": [{ label: "Manage Retreats", to: "/manageRetreat" }],
  project: [{ label: "Manage Retreats", to: "/manageRetreat" }],
  "create-program": [{ label: "Manage Retreats", to: "/manageRetreat" }],
  "create-directory": [{ label: "Manage Retreats", to: "/manageRetreat" }],
  "build-my-team": [{ label: "Manage Retreats", to: "/manageRetreat" }],
  "retreat-registrations": [
    { label: "Manage Retreats", to: "/manageRetreat" },
  ],
  "retreat-backup": [{ label: "Manage Retreats", to: "/manageRetreat" }],
  ticketReply: [{ label: "Tickets", to: "/tickets" }],
  "create-ticket": [{ label: "Tickets", to: "/tickets" }],
};

interface Crumb {
  label: string;
  to?: string;
  active?: boolean;
}

function humanizeSegment(segment: string): string {
  if (ROUTE_LABELS[segment]) return ROUTE_LABELS[segment];
  if (/^\d+$/.test(segment) || /^[0-9a-f-]{8,}$/i.test(segment)) {
    return "Details";
  }
  return segment
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[-_]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function buildCrumbs(pathname: string): Crumb[] {
  const parts = pathname.split("/").filter(Boolean);
  if (!parts.length) {
    return [{ label: "App", to: "/dashboard" }];
  }

  const crumbs: Crumb[] = [{ label: "App", to: "/dashboard" }];
  const first = parts[0];
  const parents = ROUTE_PARENTS[first] || [];

  parents.forEach((parent) => {
    crumbs.push({
      label: parent.label,
      to: parent.to || undefined,
    });
  });

  parts.forEach((part, index) => {
    const isLast = index === parts.length - 1;
    const to = isLast
      ? undefined
      : `/${parts.slice(0, index + 1).join("/")}`;
    crumbs.push({
      label: humanizeSegment(part),
      to,
      active: isLast,
    });
  });

  if (crumbs.length) {
    crumbs[crumbs.length - 1] = {
      ...crumbs[crumbs.length - 1],
      to: undefined,
      active: true,
    };
  }

  return crumbs;
}

function getPageTitle(pathname: string): string {
  const parts = pathname.split("/").filter(Boolean);
  if (!parts.length) return "Dashboard";
  const primary = parts.find(
    (p) => !/^\d+$/.test(p) && !/^[0-9a-f-]{8,}$/i.test(p)
  );
  return humanizeSegment(primary || parts[parts.length - 1]);
}

function BreadcrumbHistory(): JSX.Element {
  const location = useLocation();
  const crumbs = useMemo(
    () => buildCrumbs(location.pathname),
    [location.pathname]
  );
  const pageTitle = useMemo(
    () => getPageTitle(location.pathname),
    [location.pathname]
  );
  const hidePageTitle =
    location.pathname === "/manageRetreat" ||
    location.pathname === "/upcoming-retreats" ||
    /^\/retreat-setup(\/|$)/.test(location.pathname) ||
    /\/retreats\/(\d+\/)?project$/.test(location.pathname);

  return (
    <div className={s.root}>
      <nav className={s.crumbBar} aria-label="Breadcrumb">
        <span className={s.herePill}>
          <span className={s.hereDot} aria-hidden="true" />
          YOU ARE HERE
        </span>
        {crumbs.map((crumb, index) => (
          <React.Fragment key={`${crumb.label}-${index}`}>
            <span className={s.crumbChevron} aria-hidden="true">
              ›
            </span>
            {crumb.active || !crumb.to ? (
              <span className={s.crumbCurrent}>{crumb.label}</span>
            ) : (
              <Link to={crumb.to} className={s.crumbLink}>
                {crumb.label}
              </Link>
            )}
          </React.Fragment>
        ))}
      </nav>

      {hidePageTitle ? (
        <div id={ACTIONS_ID} className={s.pageActions} />
      ) : (
        <div className={s.pageTopLine}>
          <h1 className={s.pageTitle}>{pageTitle}</h1>
          <div id={ACTIONS_ID} className={s.pageActions} />
        </div>
      )}
    </div>
  );
}

export default BreadcrumbHistory;
